import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  LinearProgress,
  Box,
  Checkbox,
  FormControlLabel,
  Typography
} from "@mui/material";
import { ContentBuilderPage } from "../components/ContentBuilderPage";
import { useAxiosAuth } from "../hooks/useAxiosAuth";

/* --- Modal just handles form state --- */
function SimulationGeneratorModal({ open, onCancel, onGenerate, initialText }) {
  const [textToVisualize, setTextToVisualize] = useState(initialText?.length > 0 ? initialText : "Describe simulation to be generated");
  const [vizType, setVizType] = useState("simulation");
  const [vizSubtype, setVizSubtype] = useState("2d");
  const [vizName, setVizName] = useState("simulation");
  const [loading, setLoading] = useState(false);
  const [accuracyLevel, setAccuracyLevel] = useState("advanced");
  
  /* --- image-related state --- */
  const [useImage, setUseImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imageData, setImageData] = useState(null);
  const [imageMimeType, setImageMimeType] = useState(null);
  const pasteZoneRef = useRef(null);

  /* keep initial text in sync */
  useEffect(() => {
    setTextToVisualize(initialText?.length > 0 ? initialText : "Describe simulation to be generated");
  }, [initialText]);

  /* read a File (from <input> or paste) into base64 */
  const readFileAsBase64 = (file) => {
    if (!file) return;
    setImageFile(file);
    setImageMimeType(file.type);
    const reader = new FileReader();
    reader.onload = () => {
      const base64Only = reader.result.split(",")[1]; // strip “data:mime;base64,”
      setImageData(base64Only);
      setUseImage(true);
    };
    reader.readAsDataURL(file);
  };

  /* handle <input type="file"> */
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setImageFile(null);
      setImageData(null);
      setImageMimeType(null);
      return;
    }
    readFileAsBase64(file);
  };

  /* --- handle clipboard paste --- */
  const handlePaste = (e) => {
    const items = e.clipboardData?.items ?? e.nativeEvent.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file" && item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          readFileAsBase64(file);
          e.preventDefault();
        }
        break;
      }
    }
  };

  /* submit to parent */
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const options = {
        vizName,
        vizType,
        vizSubtype,
        ...(useImage && imageData
          ? { image_data: imageData, image_mime_type: imageMimeType }
          : {}),
        accuracyLevel
      };
      await onGenerate(textToVisualize, options);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Visual Simulation Generator</DialogTitle>

      <DialogContent
        onPaste={handlePaste}
        sx={{ p: 2, display: "flex", flexDirection: "column", maxHeight: "90vh" }}
      >
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* scrollable text area */}
        <Box sx={{ flex: "1 1 auto", minHeight: 0, maxHeight: "30vh", overflowY: "auto" }}>
          <TextField
            fullWidth
            multiline
            value={textToVisualize}
            onChange={(e) => setTextToVisualize(e.target.value)}
            margin="normal"
          />
        </Box>

        {/* controls */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 2,
            overflowY: "auto"
          }}
        >
          <TextField
            label="Visual Name"
            fullWidth
            value={vizName}
            onChange={(e) => setVizName(e.target.value)}
            margin="normal"
          />

          <TextField
            select
            label="Simulation Type"
            fullWidth
            value={vizSubtype}
            onChange={(e) => setVizSubtype(e.target.value)}
            margin="normal"
          >
            <MenuItem value="3d">3D Simulation</MenuItem>
            <MenuItem value="2d">2D Simulation</MenuItem>
            <MenuItem value="physics">Simulation with Physics Engine</MenuItem>
            <MenuItem value="placeholder">Placeholder</MenuItem>
          </TextField>

          {/* new accuracy level selector */}
          <TextField
            select
            label="Accuracy Level"
            fullWidth
            value={accuracyLevel}
            onChange={(e) => setAccuracyLevel(e.target.value)}
            margin="normal"
          >
            <MenuItem value="standard">Standard LLM</MenuItem>
            <MenuItem value="advanced">Advanced LLM</MenuItem>
          </TextField>

          <FormControlLabel
            control={
              <Checkbox
                checked={useImage}
                onChange={(e) => {
                  setUseImage(e.target.checked);
                  if (!e.target.checked) {
                    setImageFile(null);
                    setImageData(null);
                    setImageMimeType(null);
                  }
                }}
              />
            }
            label="Include Image (file or paste)"
          />

          {useImage && (
            <>
              <input type="file" accept="image/*" onChange={handleFileChange} />
              <Box
                ref={pasteZoneRef}
                tabIndex={0}
                contentEditable
                suppressContentEditableWarning
                onClick={() => pasteZoneRef.current.focus()}
                onPaste={handlePaste}
                sx={{
                  mt: 1,
                  p: 2,
                  maxHeight: "40vh",
                  overflowY: "auto",
                  border: 2,
                  borderStyle: "dashed",
                  borderColor: imageData ? "transparent" : "text.secondary",
                  borderRadius: 1,
                  bgcolor: imageData ? "transparent" : "action.hover",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  outline: "none",
                  caretColor: "transparent",
                  '&:focus': { borderColor: "primary.main" }
                }}
              >
                {imageData ? (
                  <Box
                    component="img"
                    src={`data:${imageMimeType};base64,${imageData}`}  
                    alt="pasted preview"
                    sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <Typography color="text.secondary">
                    Click here or focus and press Cmd+V to paste an image
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SimulationGenerator({ courseId, lessonId }) {
  const axios = useAxiosAuth();

  const generateVisualization = useCallback(
    async (
      text,
      courseId,
      lessonId,
      { vizName, vizType, vizSubtype, image_data, image_mime_type, accuracyLevel }
    ) => {
      const body = {
        concept:       text,
        course_id:     courseId,
        lesson_id:     lessonId,
        visual_type:   vizType,
        visual_subtype:vizSubtype,
        visual_name:   vizName,
        ...(image_data    ? { image_data }     : {}),
        ...(image_mime_type? { image_mime_type } : {}),
        accuracy_level:accuracyLevel
      };
      const { data } = await axios.post("/visuals/generate", body);
      return data;
    },
    [axios, courseId, lessonId]
  );

  return (
    <ContentBuilderPage
      instructionText="Highlight text to create a visual simulation."
      modal={<SimulationGeneratorModal />}
      generatorFn={generateVisualization}
      assetType="visuals"
    />
  );
}
